"use client"

import { SubmitButton } from "@/components/Inputs/button"
import {
  CustomPasswordField,
  CustomTextField,
} from "@/components/Inputs/textFields"
import AppLogoWithLabel from "@/components/SVGs/logo/logo"
import { Colors } from "@/components/Theme/colors"
import { Fonts } from "@/components/Theme/font"
import { yupResolver } from "@hookform/resolvers/yup"
import { Link } from "@mui/material"
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types"
import { useRouter } from "next/navigation"
import React from "react"
import { useForm } from "react-hook-form"
import { useDispatch, useSelector } from "react-redux"
import * as Yup from "yup"
import { userLogin } from "../../store/features/auth/authSlice"

export default function LoginForm() {
  const router = useRouter()
  const [showPassword, setShowPassword] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const { user } = useSelector((state) => state.auth)
  const dispatch = useDispatch()

  React.useEffect(() => {
    router.prefetch("/dashboard/main/budget", { kind: PrefetchKind.AUTO })
    // other prefetches
  }, [router])

  React.useEffect(() => {
    if (user?.status === 428) {
      router.push(`/auth/password/reset/${user.data.reference}`)
    }
  }, [user, router])

  const validationSchema = Yup.object().shape({
    email: Yup.string().required("Email is required").email("Email is invalid"),
    password: Yup.string().required("Password is required"),
  })

  const handleMouseDownPassword = (event) => event.preventDefault()
  const handleClickShowPassword = () => setShowPassword(!showPassword)
  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm({ resolver: yupResolver(validationSchema) })

  const onSubmit = handleSubmit((formData) => {
    setLoading(true)
    dispatch(userLogin(formData))
      .unwrap()
      .then(({ data }) => {
        setLoading(false)

        const type = data?.type?.toLowerCase()
        const role = data?.role?.toLowerCase()
        let dashboardPath = "/access-denied"
        switch (type) {
          case "government":
            switch (role) {
              case "admin":
              case "executive":
              case "director_budget":
              case "director_planning":
              case "director_ecd":
              case "consultant":
              case "governor":
              case "director_m_e":
                dashboardPath = "/dashboard/main/budget"
                break
              case "executive":
                dashboardPath = "/dashboard/main/performance"
                break
              case "planner":
              case "desk_officer":
                dashboardPath = "/dashboard/mda"
                break
              case "hod":
                dashboardPath = "/dashboard/mda"
                break
              case "support":
                dashboardPath = "/dashboard/support"
                break
              default:
                dashboardPath = "/access-denied"
            }
            break

          case "support":
            dashboardPath = "/support/dashboard"
            break

          case "partner":
            dashboardPath = "/partner/dashboard"
            break

          case "agent":
            dashboardPath = "/agents/dashboard"
            break

          default:
            dashboardPath = "/access-denied"
        }
        router.push(dashboardPath)
      })
      .catch(() => {
        setLoading(false)
      })
  })

  return (
    <div className=" w-full h-full lg:h-[660px] lg:w-[560px] bg-white pt-4 rounded-xl border border-solid border-[#E9E9E9] shadow-[0px_2px_4px_0px_#0000001A,0px_1px_10px_0px_#0000000D] flex flex-col gap-6 justify-between items-center">
      <div className="w-full flex justify-center items-center">
        <AppLogoWithLabel />
      </div>
      <div className="w-full max-w-[560px] flex flex-col justify-center items-center py-0 px-3 md:px-8 lg:px-3 xl:px-[48px] gap-8">
        <div className="w-full  flex flex-col gap-4 mb-8 mt-3">
          <h2 className="font-bold font-inter text-[28px] md:text-[36px] leading-[24px] text-[#232325] text-center">
            Welcome back!
          </h2>
          <p className="font-normal font-inter text-xs md:text-sm  text-[#232325] text-center">
            Login below to access your dashboard
          </p>
        </div>
        <form
          onSubmit={onSubmit}
          noValidate
          autoComplete="off"
          className="w-full xl:w-[464px] grid grid-cols-1 gap-6"
        >
          <CustomTextField
            name="email"
            control={control}
            type="email"
            label="Email"
            error={!!errors.email}
            helperText={errors.email?.message}
            disabled={loading}
          />

          <div className="w-full grid grid-cols-1" xs={12}>
            <CustomPasswordField
              label="Password"
              id="password"
              name="password"
              control={control}
              type={showPassword ? "text" : "password"}
              showPassword={showPassword}
              handleClickShowPassword={handleClickShowPassword}
              handleMouseDownPassword={handleMouseDownPassword}
              error={!!errors.password}
              helperText={errors.password?.message}
              disabled={loading}
            />
            <div className="w-full flex py-2 px-0 justify-end items-end">
              <Link
                underline="hover"
                sx={{
                  font: `normal normal normal 500 16px/100% ${Fonts.primary}`,
                  textTransform: "capitalize",
                  color: Colors.primaryDarkest,
                  cursor: "pointer",
                }}
                href="/auth/forgot-password"
              >
                Forgot passsword
              </Link>
            </div>
          </div>

          <SubmitButton
            className="mt-8"
            text="Login"
            loading={loading}
            disabled={loading}
          />
        </form>
      </div>
      <div className="w-full  px-4 py-1 border-t-2 border-[#0000001A] bg-[#F7F7F9] flex justify-center items-center">
        <h4 className="flex gap-3 font-inter font-normal text-base leading-[44px] text-[#232325]">
          Powered by{" "}
          <span className="text-primary_darkest font-semibold">
            ABIDE Budget.
          </span>
        </h4>
      </div>
    </div>
  )
}
